import React, { useState, useEffect, useCallback } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import toast from "react-hot-toast";

import {
  retrieveAllBlendsApi,
  retrieveBlendVersionsApi,
  retrieveVersionItemsApi,
} from "../../../components/api/BlendApiService";
import {
  retrieveBatchesByBlendVersionApi,
  deleteBatchApi,
  retrieveBatchCostComponentsApi,
  createAddBackBatchApi,
} from "../../../components/api/BatchApiService";
import { retrieveFastFilteredShippingInstructionCostingApi } from "../../../components/api/ShippingInstructionApiService";
import { retrieveAllocationsByBatchApi } from "../../../components/api/BatchAllocationApiService";
import AddEditBatchDialog from "./Dialogs/AddEditBatchDialog";

// ─── helpers ────────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const colorMap = {
    ACTIVE: "success",
    INACTIVE: "default",
    CANCELLED: "error",
  };
  return (
    <Chip
      label={status || "—"}
      color={colorMap[status] || "default"}
      size="small"
    />
  );
}

function ShipmentChip({ shippedKg, leftInStockKg, onClick }) {
  const shipped = Number(shippedKg) || 0;
  const left    = Number(leftInStockKg);
  if (shipped <= 0) return <Chip label="In Stock" color="default" size="small" />;
  const fullyShipped = !isNaN(left) && left <= 0;
  return (
    <Chip
      label={fullyShipped ? "Fully Shipped" : "Partially Shipped"}
      color={fullyShipped ? "success" : "warning"}
      size="small"
      onClick={onClick}
      sx={{ cursor: "pointer" }}
    />
  );
}

function BatchAllocationsPopover({ batchId, anchorEl, onClose }) {
  const [allocations, setAllocations] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !batchId) return;
    let cancelled = false;
    setLoading(true);
    setAllocations(null);
    retrieveAllocationsByBatchApi(batchId)
      .then((res) => { if (!cancelled) setAllocations(res?.data || []); })
      .catch(() => { if (!cancelled) toast.error("Could not load allocations."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, batchId]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box sx={{ p: 2, minWidth: 300 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Shipment Allocations</Typography>
        {loading && <CircularProgress size={20} />}
        {!loading && allocations?.length === 0 && (
          <Typography variant="body2" color="text.secondary">No allocations found.</Typography>
        )}
        {!loading && allocations && allocations.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 600 }}>SI #</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Qty (kg)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.shippingInstructionId}-{a.finalShippingInstructionNumber || a.shippingInstructionId}</TableCell>
                  <TableCell align="right">
                    {a.allocatedQuantityKg != null
                      ? Number(a.allocatedQuantityKg).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Popover>
  );
}

// ─── Batch row (expandable) ─────────────────────────────────────────────────

const fmt = (v, decimals = 2) =>
  v != null && !isNaN(Number(v))
    ? Number(v).toLocaleString("en", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : "—";

function BlendItemsPanel({ batch }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    retrieveVersionItemsApi(batch.blendId, batch.blendVersionId)
      .then((res) => setItems(res?.data || []))
      .catch(() => toast.error("Could not load blend items."))
      .finally(() => setLoading(false));
  }, [batch.blendId, batch.blendVersionId]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;
  if (!items || items.length === 0)
    return <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>No blend items.</Typography>;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: 600 }}>Item ID</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Name / Grade</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Origin</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Crop</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Qty (kg)</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Cases</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>SI</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, idx) => {
            const name = [
              item.item?.origin, item.item?.type, item.item?.buyingGrade, item.item?.crop,
            ].filter(Boolean).join(" ");
            return (
              <TableRow key={item.id || idx}>
                <TableCell>{item.item?.id || "—"}</TableCell>
                <TableCell>{name || "—"}</TableCell>
                <TableCell>{item.item?.origin || "—"}</TableCell>
                <TableCell>{item.item?.crop || "—"}</TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right">{(() => { const nwpc = Number(item.shippingInstruction?.shipmentRequest?.netWeightPerCase); return item.quantity != null && nwpc > 0 ? Math.round(Number(item.quantity) / nwpc) : "—"; })()}</TableCell>
                <TableCell>{item.unit || "—"}</TableCell>
                <TableCell>
                  {item.shippingInstruction?.finalShippingInstructionNumber ||
                    item.shippingInstructionId || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CostComponentsPanel({ batchId }) {
  const [components, setComponents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    retrieveBatchCostComponentsApi(batchId)
      .then((res) => setComponents(res?.data || []))
      .catch(() => toast.error("Could not load cost components."))
      .finally(() => setLoading(false));
  }, [batchId]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;
  if (!components || components.length === 0)
    return <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>No cost data recorded for this batch.</Typography>;

  const tobacco   = components.filter((c) => c.componentType === "TOBACCO");
  const packaging = components.filter((c) => c.componentType !== "TOBACCO");

  const tobaccoTotal   = tobacco.reduce((s, c) => s + (Number(c.totalCostEur) || 0), 0);
  const packagingTotal = packaging.reduce((s, c) => s + (Number(c.totalCostEur) || 0), 0);
  const grandTotal     = tobaccoTotal + packagingTotal;

  return (
    <Box>
      {/* ── Tobacco / blend items ── */}
      {tobacco.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600, color: "text.secondary" }}>
            Tobacco / Blend Items
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f9fbe7" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Item Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Total Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Price (EUR)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customs Duty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Customs EUR</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: "#f0f4c3" }} align="right">Total Cost EUR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tobacco.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{c.itemCode || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{c.description || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.quantity, 3)}</TableCell>
                    <TableCell>{c.unit || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.unitCostOriginal)}</TableCell>
                    <TableCell>{c.currency || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.totalPriceOriginal)}</TableCell>
                    <TableCell>{c.totalPriceCurrency || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.totalPriceEur)}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{c.customsDuty || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{c.customsDutyRate || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.customsDutyEur)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#f9fbe7" }}>{fmt(c.totalCostEur)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#f0f4c3" }}>
                  <TableCell colSpan={12} align="right" sx={{ fontWeight: 700 }}>Tobacco Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(tobaccoTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Non-tobacco materials ── */}
      {packaging.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: "text.secondary" }}>
            Non-Tobacco Materials
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#e3f2fd" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Total Price</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: "#bbdefb" }} align="right">Price (EUR)</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: "#bbdefb" }} align="right">Total Cost EUR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packaging.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{c.itemCode || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{c.description || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.quantity, 3)}</TableCell>
                    <TableCell>{c.unit || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.unitCostOriginal)}</TableCell>
                    <TableCell>{c.currency || "—"}</TableCell>
                    <TableCell align="right">{fmt(c.totalPriceOriginal)}</TableCell>
                    <TableCell>{c.totalPriceCurrency || "—"}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: "#e3f2fd" }}>{fmt(c.totalPriceEur)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "#e3f2fd" }}>{fmt(c.totalCostEur)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#bbdefb" }}>
                  <TableCell colSpan={9} align="right" sx={{ fontWeight: 700 }}>Non-Tobacco Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(packagingTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Grand total ── */}
      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Paper variant="outlined" sx={{ display: "inline-flex", px: 2, py: 0.75, gap: 3, bgcolor: "#fffde7" }}>
          <Typography variant="body2"><strong>Tobacco:</strong> {fmt(tobaccoTotal)} EUR</Typography>
          <Typography variant="body2"><strong>Non-Tobacco:</strong> {fmt(packagingTotal)} EUR</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}><strong>Grand Total:</strong> {fmt(grandTotal)} EUR</Typography>
        </Paper>
      </Box>
    </Box>
  );
}

function PlannedCostsPanel({ batch }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [openSiId, setOpenSiId] = useState(null); // which SI detail row is expanded

  useEffect(() => {
    setLoading(true);
    setError(null);
    retrieveVersionItemsApi(batch.blendId, batch.blendVersionId)
      .then((res) => {
        const items = res?.data || [];
        const siIds = [
          ...new Set(
            items
              .map((i) => i.shippingInstruction?.id ?? i.shippingInstructionId)
              .filter(Boolean)
          ),
        ];
        if (siIds.length === 0) {
          setData({ items, siMap: {} });
          return;
        }
        return retrieveFastFilteredShippingInstructionCostingApi({
          shippingInstructionIdList: siIds,
          recordStatusList: ["ACTIVE"],
        }).then((siRes) => {
          const siMap = {};
          (siRes?.data || []).forEach((si) => { siMap[si.id] = si; });
          setData({ items, siMap });
        });
      })
      .catch(() => setError("Could not load planned cost data."))
      .finally(() => setLoading(false));
  }, [batch.blendId, batch.blendVersionId]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;
  if (error)   return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (!data)   return null;

  const eurHuf  = Number(batch.productionFxRateToEur)    || 0;
  const usdHuf  = Number(batch.productionFxRateUsdToHuf) || 0;
  const usdToEur = eurHuf > 0 && usdHuf > 0 ? usdHuf / eurHuf : null;

  const rows = data.items.map((item) => {
    const siId = item.shippingInstruction?.id ?? item.shippingInstructionId;
    const si   = siId ? data.siMap[siId] : null;
    if (!si) return { item, si: null, unitCostUsd: null, totalCostUsd: null, totalCostEur: null };

    const isFW     = si.shipmentRequestType?.startsWith("FW");
    const buyingUsd = si.actualBuyingPriceUsd ?? si.purchaseOrderUsdFinalPrice ?? 0;
    const unitCostUsd = [
      buyingUsd,
      si.actualInsuranceCostUsd,
      si.actualFreightPriceUsd,
      si.secondFreightCostUsd,
      si.importExportExpenseUsd,
      si.demurrageUsd,
      si.fumigationUsd,
      si.handlingUsd,
      si.debitNoteUsd,
      -(si.creditNoteUsd || 0),
    ].reduce((s, v) => s + (Number(v) || 0), 0);

    const qty          = Number(item.quantity) || 0;
    const totalCostUsd = unitCostUsd * qty;
    const totalCostEur = usdToEur != null ? totalCostUsd * usdToEur : null;
    return { item, si, isFW, unitCostUsd, totalCostUsd, totalCostEur };
  });

  const plannedTotalUsd  = rows.reduce((s, r) => s + (r.totalCostUsd || 0), 0);
  const plannedTotalEur  = usdToEur != null ? plannedTotalUsd * usdToEur : null;
  const totalQty         = Number(batch.dietOutNet) || 0;
  const plannedUnitUsd   = totalQty > 0 ? plannedTotalUsd / totalQty : null;
  const plannedUnitEur   = plannedUnitUsd != null && usdToEur != null ? plannedUnitUsd * usdToEur : null;
  const actualUnitEur    = batch.unitPriceEur   != null ? Number(batch.unitPriceEur)   : null;
  const actualTobaccoEur = batch.tobaccoPgEur   != null ? Number(batch.tobaccoPgEur)   : null;
  const diffEur          = plannedUnitEur != null && actualUnitEur != null ? actualUnitEur - plannedUnitEur : null;

  return (
    <Box>
      {/* FX info bar */}
      {eurHuf > 0 && usdHuf > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          FX rates used: EUR/HUF {eurHuf} · USD/HUF {usdHuf}
          {batch.productionFxRateDate
            ? ` (MNB date: ${format(new Date(batch.productionFxRateDate), "dd/MM/yyyy")})`
            : ""}
        </Typography>
      ) : (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          No FX rates recorded for this batch — EUR conversion unavailable. USD totals shown only.
        </Alert>
      )}

      {/* Per-item breakdown */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3e5f5" }}>
              <TableCell sx={{ width: 32 }} />
              <TableCell sx={{ fontWeight: 600 }}>SI #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Grade Mark</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Qty (kg)</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Unit Cost (USD/kg)</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Total (USD)</TableCell>
              {usdToEur != null && (
                <TableCell sx={{ fontWeight: 600 }} align="right">Total (EUR)</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const siId = r.si?.id;
              const isOpen = openSiId === siId;
              // Build cost-line breakdown for the detail panel
              const costLines = r.si ? [
                { label: "Buying price",          value: r.si.actualBuyingPriceUsd ?? r.si.purchaseOrderUsdFinalPrice },
                { label: "Insurance",             value: r.si.actualInsuranceCostUsd },
                { label: "Freight",               value: r.si.actualFreightPriceUsd },
                { label: "2nd Freight",           value: r.si.secondFreightCostUsd },
                { label: "Import/Export expense", value: r.si.importExportExpenseUsd },
                { label: "Demurrage",             value: r.si.demurrageUsd },
                { label: "Fumigation",            value: r.si.fumigationUsd },
                { label: "Handling",              value: r.si.handlingUsd },
                { label: "Debit note",            value: r.si.debitNoteUsd },
                { label: "Credit note",           value: r.si.creditNoteUsd ? -(r.si.creditNoteUsd) : null },
              ].filter((l) => l.value != null && Number(l.value) !== 0) : [];

              return (
                <React.Fragment key={i}>
                  <TableRow
                    hover
                    sx={{ bgcolor: !r.si ? "rgba(255,152,0,0.05)" : undefined }}
                  >
                    <TableCell sx={{ p: 0.5 }}>
                      {r.si && costLines.length > 0 && (
                        <Tooltip title={isOpen ? "Hide SI detail" : "Show SI calculation"}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenSiId(isOpen ? null : siId)}
                          >
                            {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {r.si?.finalShippingInstructionNumber ?? (r.si ? r.si.id : "—")}
                      {r.isFW && (
                        <Chip label="FW" size="small" sx={{ ml: 0.5, height: 16, fontSize: "0.65rem" }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {r.item?.shippingInstruction?.shipmentRequest?.customerGradeMark ?? "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {[r.item?.item?.origin, r.item?.item?.type, r.item?.item?.buyingGrade, r.item?.item?.crop]
                        .filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell align="right">{fmt(r.item?.quantity, 2)}</TableCell>
                    <TableCell align="right">{r.unitCostUsd != null ? fmt(r.unitCostUsd, 4) : "—"}</TableCell>
                    <TableCell align="right">{r.totalCostUsd != null ? fmt(r.totalCostUsd, 2) : "—"}</TableCell>
                    {usdToEur != null && (
                      <TableCell align="right">{r.totalCostEur != null ? fmt(r.totalCostEur, 2) : "—"}</TableCell>
                    )}
                  </TableRow>

                  {/* Collapsible SI cost breakdown */}
                  {r.si && (
                    <TableRow>
                      <TableCell
                        colSpan={usdToEur != null ? 8 : 7}
                        sx={{ py: 0, pl: 5, bgcolor: "#fafafa" }}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              SI cost breakdown — unit costs (USD/kg)
                            </Typography>
                            <Table size="small" sx={{ width: "auto", minWidth: 380 }}>
                              <TableBody>
                                {costLines.map((line, li) => (
                                  <TableRow key={li}>
                                    <TableCell sx={{ py: 0.25, fontSize: "0.75rem", color: "text.secondary", border: "none", pl: 0 }}>
                                      {line.label}
                                    </TableCell>
                                    <TableCell align="right" sx={{ py: 0.25, fontSize: "0.75rem", fontFamily: "monospace", border: "none", pr: 0 }}>
                                      {Number(line.value) < 0
                                        ? `−${fmt(Math.abs(Number(line.value)), 4)}`
                                        : fmt(Number(line.value), 4)}{" USD"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow>
                                  <TableCell sx={{ py: 0.25, fontWeight: 700, fontSize: "0.75rem", borderTop: "1px solid #e0e0e0", pl: 0 }}>
                                    Total unit cost
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.25, fontWeight: 700, fontSize: "0.75rem", fontFamily: "monospace", borderTop: "1px solid #e0e0e0", pr: 0 }}>
                                    {fmt(r.unitCostUsd, 4)} USD/kg
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ py: 0.25, fontSize: "0.75rem", color: "text.secondary", border: "none", pl: 0 }}>
                                    × {fmt(r.item?.quantity, 2)} kg
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.25, fontWeight: 700, fontSize: "0.75rem", fontFamily: "monospace", border: "none", pr: 0, color: "primary.main" }}>
                                    = {fmt(r.totalCostUsd, 2)} USD
                                    {r.totalCostEur != null && ` ≈ ${fmt(r.totalCostEur, 2)} EUR`}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            <TableRow sx={{ bgcolor: "#ede7f6" }}>
              <TableCell colSpan={6} align="right" sx={{ fontWeight: 700 }}>
                Planned Tobacco Total
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {fmt(plannedTotalUsd, 2)} USD
              </TableCell>
              {usdToEur != null && (
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {plannedTotalEur != null ? `${fmt(plannedTotalEur, 2)} EUR` : "—"}
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Planned vs Actual comparison */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Planned vs Actual — Unit Price
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Planned Unit Price
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {plannedUnitUsd != null ? `${fmt(plannedUnitUsd, 4)} USD/kg` : "—"}
            </Typography>
            {plannedUnitEur != null && (
              <Typography variant="body2" color="text.secondary">
                ≈ {fmt(plannedUnitEur, 4)} EUR/kg
              </Typography>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{ p: 1.5, minWidth: 190, bgcolor: actualUnitEur != null ? "#f9fbe7" : undefined }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Actual Unit Price
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {actualUnitEur != null ? `${fmt(actualUnitEur, 4)} EUR/kg` : "—"}
            </Typography>
            {actualTobaccoEur != null && (
              <Typography variant="body2" color="text.secondary">
                Tobacco total: {fmt(actualTobaccoEur, 2)} EUR
              </Typography>
            )}
          </Paper>

          {diffEur != null && (
            <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Difference (Actual − Planned)
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color={diffEur > 0 ? "error.main" : "success.main"}
              >
                {diffEur > 0 ? "+" : ""}{fmt(diffEur, 4)} EUR/kg
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {diffEur > 0 ? "over plan" : "under plan"}
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function ProcessInfoPanel({ batch }) {
  const fmtDate = (v) => {
    if (!v) return "—";
    try { return format(new Date(v), "dd.MM.yyyy"); } catch { return String(v); }
  };
  const fmtKg = (v) => v != null && !isNaN(Number(v))
    ? `${Number(v).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
    : "—";

  const rows = [
    { label: "Production ID",   primary: batch.primaryId,            diet: batch.dietId },
    { label: "Blend ID (Code)", primary: batch.primaryCode,          diet: batch.dietCode },
    { label: "Blend Name",      primary: batch.primaryGradeName,     diet: batch.dietGradeName },
    { label: "Processing Date", primary: fmtDate(batch.primaryProcessingDate), diet: fmtDate(batch.processingDate ?? batch.dietProcessingDate) },
    { label: "Input",           primary: fmtKg(batch.rawIn),         diet: fmtKg(batch.dietIn) },
    { label: "Output",          primary: fmtKg(batch.primaryOut),    diet: fmtKg(batch.dietOutNet) },
    {
      label: "Processing Fee",
      primary: "—",
      diet: batch.processingFeeEur != null && Number(batch.dietOutNet) > 0
        ? `${(Number(batch.processingFeeEur) / Number(batch.dietOutNet)).toFixed(4)} EUR/kg (${Number(batch.processingFeeEur).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR total)`
        : "—",
    },
  ];

  const hasAny = rows.some((r) => r.primary !== "—" || r.diet !== "—");

  if (!hasAny) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
        No process info saved yet. Edit the batch to add it.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr", gap: 0 }}>
      {/* Header */}
      <Box sx={{ p: 1, fontWeight: 700, fontSize: "0.78rem", color: "text.secondary", borderBottom: "2px solid #e0e0e0" }} />
      {["Primary Process", "DIET Process"].map((h) => (
        <Box key={h} sx={{ p: 1, fontWeight: 700, fontSize: "0.8rem", borderBottom: "2px solid #e0e0e0", bgcolor: h.startsWith("Primary") ? "#f3f9f3" : "#f0f4ff" }}>
          {h}
        </Box>
      ))}
      {/* Rows */}
      {rows.map((r, i) => (
        <React.Fragment key={r.label}>
          <Box sx={{ p: 0.75, fontSize: "0.78rem", color: "text.secondary", bgcolor: i % 2 === 0 ? "#fafafa" : "#fff", borderBottom: "1px solid #f0f0f0", fontWeight: 600 }}>
            {r.label}
          </Box>
          <Box sx={{ p: 0.75, fontSize: "0.82rem", fontFamily: r.label === "Production ID" || r.label === "Blend ID (Code)" ? "monospace" : "inherit", bgcolor: i % 2 === 0 ? "#f6fbf6" : "#fafff9", borderBottom: "1px solid #f0f0f0" }}>
            {r.primary || "—"}
          </Box>
          <Box sx={{ p: 0.75, fontSize: "0.82rem", fontFamily: r.label === "Production ID" || r.label === "Blend ID (Code)" ? "monospace" : "inherit", bgcolor: i % 2 === 0 ? "#f2f5ff" : "#f8faff", borderBottom: "1px solid #f0f0f0" }}>
            {r.diet || "—"}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}

function PlannedVsActualPanel({ batch }) {
  const [siData, setSiData]         = useState(null);
  const [components, setComponents] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      retrieveVersionItemsApi(batch.blendId, batch.blendVersionId).then((res) => {
        const items = res?.data || [];
        const siIds = [
          ...new Set(
            items
              .map((i) => i.shippingInstruction?.id ?? i.shippingInstructionId)
              .filter(Boolean)
          ),
        ];
        if (siIds.length === 0) return { items, siMap: {} };
        return retrieveFastFilteredShippingInstructionCostingApi({
          shippingInstructionIdList: siIds,
          recordStatusList: ["ACTIVE"],
        }).then((siRes) => {
          const siMap = {};
          (siRes?.data || []).forEach((si) => { siMap[si.id] = si; });
          return { items, siMap };
        });
      }),
      retrieveBatchCostComponentsApi(batch.id).then((res) => res?.data || []),
    ])
      .then(([sd, comps]) => { setSiData(sd); setComponents(comps); })
      .catch(() => setError("Could not load comparison data."))
      .finally(() => setLoading(false));
  }, [batch.blendId, batch.blendVersionId, batch.id]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;
  if (error)   return <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>;
  if (!siData || !components) return null;

  const eurHuf   = Number(batch.productionFxRateToEur)    || 0;
  const usdHuf   = Number(batch.productionFxRateUsdToHuf) || 0;
  const usdToEur = eurHuf > 0 && usdHuf > 0 ? usdHuf / eurHuf : null;

  // Build actual lookup: itemCode → tobacco component
  const actualByCode = {};
  components.forEach((c) => {
    if (c.componentType === "TOBACCO" && c.itemCode) {
      actualByCode[String(c.itemCode)] = c;
    }
  });

  // Build merged rows — one per blend version item
  const rows = siData.items.map((item) => {
    const siId = item.shippingInstruction?.id ?? item.shippingInstructionId;
    const si   = siId ? siData.siMap[siId] : null;

    // Planned cost
    let unitCostUsd  = null;
    let plannedUsd   = null;
    if (si) {
      const buyingUsd = si.actualBuyingPriceUsd ?? si.purchaseOrderUsdFinalPrice ?? 0;
      unitCostUsd = [
        buyingUsd, si.actualInsuranceCostUsd, si.actualFreightPriceUsd,
        si.secondFreightCostUsd, si.importExportExpenseUsd, si.demurrageUsd,
        si.fumigationUsd, si.handlingUsd, si.debitNoteUsd, -(si.creditNoteUsd || 0),
      ].reduce((s, v) => s + (Number(v) || 0), 0);
      const qty = Number(item.quantity) || 0;
      plannedUsd = unitCostUsd * qty;
    }

    // Actual component matched by SI customer grade mark (= BatchCostComponent.itemCode from Excel col A)
    const itemCode = item.shippingInstruction?.shipmentRequest?.customerGradeMark ?? null;
    const actual   = itemCode ? actualByCode[itemCode] : null;
    const actualUsd = actual && usdToEur != null ? (Number(actual.totalCostEur) || 0) / usdToEur : null;

    const variance = plannedUsd != null && actualUsd != null ? actualUsd - plannedUsd : null;

    // SO Final Price
    const soFinalPriceUsd = si ? (Number(si.salesOrderUsdFinalPrice) || null) : null;
    const qty             = Number(item.quantity) || 0;
    const soRevenueTotalUsd = soFinalPriceUsd != null ? soFinalPriceUsd * qty : null;
    const soVsActualVariance = soRevenueTotalUsd != null && actualUsd != null ? soRevenueTotalUsd - actualUsd : null;

    return { item, si, itemCode, unitCostUsd, plannedUsd, actualUsd, actual, variance, soFinalPriceUsd, soRevenueTotalUsd, soVsActualVariance };
  });

  // Actual tobacco components with no matching blend item (unmatched)
  const matchedCodes = new Set(rows.map((r) => r.itemCode).filter(Boolean));
  const unmatchedActual = components.filter(
    (c) => c.componentType === "TOBACCO" && c.itemCode && !matchedCodes.has(String(c.itemCode))
  );
  const nonTobaccoComps = components.filter((c) => c.componentType !== "TOBACCO");

  // Totals
  const totalPlannedUsd   = rows.reduce((s, r) => s + (r.plannedUsd  || 0), 0);
  const totalActualTobUsd = rows.reduce((s, r) => s + (r.actualUsd   || 0), 0)
    + (usdToEur != null ? unmatchedActual.reduce((s, c) => s + (Number(c.totalCostEur) || 0) / usdToEur, 0) : 0);
  const totalNonTobUsd   = usdToEur != null
    ? nonTobaccoComps.reduce((s, c) => s + (Number(c.totalCostEur) || 0) / usdToEur, 0) : 0;
  const totalVariance    = totalActualTobUsd > 0 && totalPlannedUsd > 0
    ? totalActualTobUsd - totalPlannedUsd : null;
  const totalSoRevenueUsd = rows.some((r) => r.soRevenueTotalUsd != null)
    ? rows.reduce((s, r) => s + (r.soRevenueTotalUsd || 0), 0)
    : null;
  const totalSoVsActualVariance = totalSoRevenueUsd != null && totalActualTobUsd > 0
    ? totalSoRevenueUsd - totalActualTobUsd
    : null;

  const totalQty     = Number(batch.dietOutNet) || 0;
  const plannedUnit  = totalQty > 0 && totalPlannedUsd > 0 ? totalPlannedUsd / totalQty : null;
  const actualUnit   = batch.unitPriceEur != null && usdToEur != null
    ? Number(batch.unitPriceEur) / usdToEur
    : (totalQty > 0 && totalActualTobUsd > 0 ? totalActualTobUsd / totalQty : null);
  const unitVariance = plannedUnit != null && actualUnit != null ? actualUnit - plannedUnit : null;

  const varColor    = (v) => (v == null ? undefined : v > 0 ? "error.main" : "success.main");
  const varColorInv = (v) => (v == null ? undefined : v > 0 ? "success.main" : "error.main");
  const varSign     = (v) => (v > 0 ? "+" : "");

  return (
    <Box>
      {/* FX bar */}
      {eurHuf > 0 && usdHuf > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          FX: EUR/HUF {eurHuf} · USD/HUF {usdHuf}
          {batch.productionFxRateDate
            ? ` (${format(new Date(batch.productionFxRateDate), "dd/MM/yyyy")})`
            : ""}
        </Typography>
      ) : (
        <Alert severity="warning" sx={{ mb: 1.5 }}>No FX rates — EUR conversion unavailable.</Alert>
      )}

      {/* Summary cards */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">Planned Tobacco</Typography>
          <Typography variant="h6" fontWeight={700}>{fmt(totalPlannedUsd, 2)} USD</Typography>
          {plannedUnit != null && (
            <Typography variant="body2" color="text.secondary">{fmt(plannedUnit, 4)} USD/kg</Typography>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1, bgcolor: totalActualTobUsd > 0 ? "#f9fbe7" : undefined }}>
          <Typography variant="caption" color="text.secondary" display="block">Actual Tobacco</Typography>
          <Typography variant="h6" fontWeight={700}>{totalActualTobUsd > 0 ? `${fmt(totalActualTobUsd, 2)} USD` : "—"}</Typography>
          {actualUnit != null && (
            <Typography variant="body2" color="text.secondary">{fmt(actualUnit, 4)} USD/kg</Typography>
          )}
        </Paper>
        {totalVariance != null && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">Variance (Actual − Planned)</Typography>
            <Typography variant="h6" fontWeight={700} color={varColor(totalVariance)}>
              {varSign(totalVariance)}{fmt(totalVariance, 2)} USD
            </Typography>
            {unitVariance != null && (
              <Typography variant="body2" color={varColor(unitVariance)}>
                {varSign(unitVariance)}{fmt(unitVariance, 4)} USD/kg
              </Typography>
            )}
          </Paper>
        )}
        {totalNonTobUsd > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1, bgcolor: "#e3f2fd" }}>
            <Typography variant="caption" color="text.secondary" display="block">Non-Tobacco (actual)</Typography>
            <Typography variant="h6" fontWeight={700}>{fmt(totalNonTobUsd, 2)} USD</Typography>
            <Typography variant="body2" color="text.secondary">Grand total: {fmt(totalActualTobUsd + totalNonTobUsd, 2)} USD</Typography>
          </Paper>
        )}
        {totalSoRevenueUsd != null && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1, bgcolor: "#e8f5e9" }}>
            <Typography variant="caption" color="text.secondary" display="block">SO Revenue (selling)</Typography>
            <Typography variant="h6" fontWeight={700}>{fmt(totalSoRevenueUsd, 2)} USD</Typography>
            {totalQty > 0 && (
              <Typography variant="body2" color="text.secondary">
                {fmt(totalSoRevenueUsd / totalQty, 4)} USD/kg
              </Typography>
            )}
            {totalActualTobUsd > 0 && totalSoRevenueUsd > 0 && (
              <Typography
                variant="body2"
                fontWeight={600}
                color={totalSoRevenueUsd - totalActualTobUsd > 0 ? "success.main" : "error.main"}
              >
                Margin: {fmt(totalSoRevenueUsd - totalActualTobUsd, 2)} USD
              </Typography>
            )}
          </Paper>
        )}
        {totalSoVsActualVariance != null && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 190, flex: 1, bgcolor: totalSoVsActualVariance >= 0 ? "#f1f8e9" : "#fce4ec" }}>
            <Typography variant="caption" color="text.secondary" display="block">SO vs Actual Variance</Typography>
            <Typography variant="h6" fontWeight={700} color={varColorInv(totalSoVsActualVariance)}>
              {varSign(totalSoVsActualVariance)}{fmt(totalSoVsActualVariance, 2)} USD
            </Typography>
            {totalQty > 0 && (
              <Typography variant="body2" color={varColorInv(totalSoVsActualVariance / totalQty)}>
                {varSign(totalSoVsActualVariance / totalQty)}{fmt(totalSoVsActualVariance / totalQty, 4)} USD/kg
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {totalSoVsActualVariance >= 0 ? "Revenue exceeds cost" : "Cost exceeds revenue"}
            </Typography>
          </Paper>
        )}
      </Stack>

      {/* Merged item-by-item table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3e5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Item Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>SI #</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Qty (kg)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Unit cost (USD/kg)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Planned (USD)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Actual (USD)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Variance</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>SO Price (USD/kg)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>SO Revenue (USD)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>SO vs Actual</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                  {r.itemCode ?? "—"}
                </TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {/* prefer actual description (matches the ERP doc), fall back to SI item fields */}
                  {r.actual?.description ||
                    [r.item?.item?.origin, r.item?.item?.type, r.item?.item?.buyingGrade, r.item?.item?.crop]
                      .filter(Boolean).join(" ") || "—"}
                </TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>
                  {r.si?.finalShippingInstructionNumber ?? (r.item?.shippingInstructionId ?? "—")}
                </TableCell>
                <TableCell align="right">{fmt(r.item?.quantity, 2)}</TableCell>
                <TableCell align="right">{r.unitCostUsd != null ? fmt(r.unitCostUsd, 4) : "—"}</TableCell>
                <TableCell align="right">{r.plannedUsd != null ? fmt(r.plannedUsd, 2) : "—"}</TableCell>
                <TableCell align="right">{r.actualUsd != null ? fmt(r.actualUsd, 2) : "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: varColor(r.variance) }}>
                  {r.variance != null ? `${varSign(r.variance)}${fmt(r.variance, 2)}` : "—"}
                </TableCell>
                <TableCell align="right">{r.soFinalPriceUsd != null ? fmt(r.soFinalPriceUsd, 4) : "—"}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "success.dark" }}>
                  {r.soRevenueTotalUsd != null ? fmt(r.soRevenueTotalUsd, 2) : "—"}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: varColorInv(r.soVsActualVariance) }}>
                  {r.soVsActualVariance != null ? `${varSign(r.soVsActualVariance)}${fmt(r.soVsActualVariance, 2)}` : "—"}
                </TableCell>
              </TableRow>
            ))}

            {/* Unmatched actual tobacco rows (in actual but not in blend version items) */}
            {unmatchedActual.map((c) => (
              <TableRow key={c.id} hover sx={{ bgcolor: "rgba(255,152,0,0.06)" }}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{c.itemCode}</TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>{c.description || "—"}</TableCell>
                <TableCell>—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">{usdToEur != null ? fmt((Number(c.totalCostEur) || 0) / usdToEur, 2) : "—"}</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
              </TableRow>
            ))}

            {/* Total row */}
            <TableRow sx={{ bgcolor: "#ede7f6" }}>
              <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>Tobacco Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalPlannedUsd, 2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{totalActualTobUsd > 0 ? fmt(totalActualTobUsd, 2) : "—"}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: varColor(totalVariance) }}>
                {totalVariance != null ? `${varSign(totalVariance)}${fmt(totalVariance, 2)}` : "—"}
              </TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700, color: "success.dark" }}>
                {totalSoRevenueUsd != null ? fmt(totalSoRevenueUsd, 2) : "—"}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: varColorInv(totalSoVsActualVariance) }}>
                {totalSoVsActualVariance != null ? `${varSign(totalSoVsActualVariance)}${fmt(totalSoVsActualVariance, 2)}` : "—"}
              </TableCell>
            </TableRow>

            {/* Non-tobacco rows */}
            {nonTobaccoComps.length > 0 && (
              <>
                {nonTobaccoComps.map((c) => (
                  <TableRow key={c.id} hover sx={{ bgcolor: "rgba(33,150,243,0.04)" }}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{c.itemCode || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{c.description || "—"}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right">{fmt(c.quantity, 3)}</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">{usdToEur != null ? fmt((Number(c.totalCostEur) || 0) / usdToEur, 2) : "—"}</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#bbdefb" }}>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: 700 }}>Non-Tobacco Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalNonTobUsd, 2)}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {unmatchedActual.length > 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
          ⚠ {unmatchedActual.length} actual component(s) could not be matched to a blend item by item code.
        </Typography>
      )}
    </Box>
  );
}

// Default admin cost per kg — used as fallback for records created before this field was introduced
const DEFAULT_ADMIN_COST_EUR_PER_KG = 0.95;

function BatchCostSummaryPanel({ batch }) {
  const [components, setComponents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    retrieveBatchCostComponentsApi(batch.id)
      .then((res) => setComponents(res?.data || []))
      .catch(() => toast.error("Could not load cost components."))
      .finally(() => setLoading(false));
  }, [batch.id]);

  if (loading) return <CircularProgress size={20} sx={{ m: 1 }} />;

  const dietOutNet = Number(batch.dietOutNet) || 0;
  const rawIn      = Number(batch.rawIn)      || 0;
  const wastage    = rawIn > 0 ? ((dietOutNet - rawIn) / dietOutNet) * 100 : null;

  // ── From cost components ──
  let tobaccoNet  = 0;
  let customsTax  = 0;
  let packaging   = 0;

  if (components && components.length > 0) {
    components.forEach((c) => {
      if (c.componentType === "TOBACCO") {
        tobaccoNet += Number(c.totalCostEur)    || 0;
        customsTax += Number(c.customsDutyEur)  || 0;
      } else {
        packaging  += Number(c.totalCostEur)    || 0;
      }
    });
    // tobaccoNet from components already includes customs — separate it
    tobaccoNet = tobaccoNet - customsTax;
  } else {
    // Fall back to batch-level stored fields when components are absent
    const storedTobacco   = Number(batch.tobaccoPgEur)    || 0;
    const storedProcFee   = Number(batch.processingFeeEur) || 0;
    const storedUnitPrice = Number(batch.unitPriceEur)    || 0;
    tobaccoNet = storedTobacco;
    packaging  = dietOutNet > 0 && storedUnitPrice > 0
      ? Math.max(0, storedUnitPrice * dietOutNet - storedTobacco - storedProcFee)
      : 0;
  }

  const processing   = Number(batch.processingFeeEur) || 0;
  const adminCostRate = Number(batch.adminCostEurPerKg) > 0
    ? Number(batch.adminCostEurPerKg)
    : DEFAULT_ADMIN_COST_EUR_PER_KG;
  const adminTotal   = adminCostRate * dietOutNet;
  const totalWithoutAdmin = tobaccoNet + customsTax + packaging + processing;
  const tobaccoTotal = tobaccoNet + customsTax;
  const grandTotal   = totalWithoutAdmin + adminTotal;
  const unitPrice    = dietOutNet > 0 ? grandTotal / dietOutNet : null;

  const perKg = (v) => (dietOutNet > 0 ? v / dietOutNet : null);

  const rows = [
    { label: "Tobacco (net)",  total: tobaccoNet,  unit: perKg(tobaccoNet),  color: "#f9fbe7", headerBg: "#f0f4c3" },
    { label: "Custom Tax",     total: customsTax,  unit: perKg(customsTax),  color: "#fff8e1", headerBg: "#ffe082" },
    { label: "Packaging",      total: packaging,   unit: perKg(packaging),   color: "#e3f2fd", headerBg: "#bbdefb" },
    { label: "Processing Fee", total: processing,  unit: perKg(processing),  color: "#f3e5f5", headerBg: "#ce93d8" },
    {
      label: `Admin Cost (${fmt(adminCostRate, 2)} EUR/kg)`,
      total: adminTotal,
      unit: adminCostRate,
      color: "#fce4ec",
      headerBg: "#f48fb1",
      isFixed: true,
    },
  ];

  return (
    <Box>
      {/* ── Header info bar ── */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {rawIn > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary" display="block">RAW In</Typography>
            <Typography variant="body1" fontWeight={700}>{fmt(rawIn, 0)} kg</Typography>
          </Paper>
        )}
        {dietOutNet > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
            <Typography variant="caption" color="text.secondary" display="block">DIET Out Net</Typography>
            <Typography variant="body1" fontWeight={700}>{fmt(dietOutNet, 0)} kg</Typography>
          </Paper>
        )}
        {wastage != null && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150, bgcolor: wastage > 15 ? "#fce4ec" : "#f9fbe7" }}>
            <Typography variant="caption" color="text.secondary" display="block">Wastage</Typography>
            <Typography variant="body1" fontWeight={700} color={wastage > 15 ? "error.main" : "text.primary"}>
              {fmt(wastage, 2)} %
            </Typography>
          </Paper>
        )}
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 170, bgcolor: "#f9fbe7" }}>
          <Typography variant="caption" color="text.secondary" display="block">Total Cost (excl. admin)</Typography>
          <Typography variant="body1" fontWeight={700}>{fmt(totalWithoutAdmin, 2)} EUR</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 170, bgcolor: "#e8f5e9" }}>
          <Typography variant="caption" color="text.secondary" display="block">Grand Total (incl. admin)</Typography>
          <Typography variant="body1" fontWeight={700}>{fmt(grandTotal, 2)} EUR</Typography>
        </Paper>
        {unitPrice != null && (
          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 180, bgcolor: "#ede7f6" }}>
            <Typography variant="caption" color="text.secondary" display="block">Total Unit Price (incl. admin)</Typography>
            <Typography variant="body1" fontWeight={700}>{fmt(unitPrice, 4)} EUR/kg</Typography>
          </Paper>
        )}
      </Stack>

      {/* ── Cost breakdown table ── */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Cost Component</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total (EUR)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>EUR / kg</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "text.secondary" }}>% of Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.label} sx={{ bgcolor: r.color }}>
                <TableCell sx={{ fontSize: "0.82rem" }}>
                  {r.label}
                  {r.isFixed && (
                    <Chip label="fixed" size="small" sx={{ ml: 1, height: 16, fontSize: "0.62rem", bgcolor: "#f06292", color: "#fff" }} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmt(r.total, 2)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                  {r.unit != null ? fmt(r.unit, 4) : "—"}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {grandTotal > 0 ? `${fmt((r.total / grandTotal) * 100, 1)} %` : "—"}
                </TableCell>
              </TableRow>
            ))}

            {/* Subtotal before admin */}
            <TableRow sx={{ bgcolor: "#f9fbe7" }}>
              <TableCell sx={{ fontWeight: 700 }}>Total (excl. admin)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontFamily: "monospace" }}>{fmt(totalWithoutAdmin, 2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                {dietOutNet > 0 ? fmt(totalWithoutAdmin / dietOutNet, 4) : "—"}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {grandTotal > 0 ? `${fmt((totalWithoutAdmin / grandTotal) * 100, 1)} %` : "—"}
              </TableCell>
            </TableRow>

            {/* Grand total row */}
            <TableRow sx={{ bgcolor: "#e8f5e9" }}>
              <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontFamily: "monospace" }}>{fmt(grandTotal, 2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                {unitPrice != null ? fmt(unitPrice, 4) : "—"}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>100 %</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Stored unit price comparison ── */}
      {batch.unitPriceEur != null && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Stored unit price (excl. admin): <strong>{fmt(Number(batch.unitPriceEur), 4)} EUR/kg</strong>
            </Typography>
            {unitPrice != null && (
              <Typography variant="caption" color="text.secondary">
                · With admin: <strong>{fmt(unitPrice, 4)} EUR/kg</strong>
                {" "}({fmt(unitPrice - Number(batch.unitPriceEur), 4)} difference)
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {!components?.length && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
          ⚠ No cost components found — tobacco and packaging values derived from stored batch totals. Upload components for full breakdown.
        </Typography>
      )}
    </Box>
  );
}

const toCases = (kg, nwpc) => {
  const w = Number(nwpc);
  return kg != null && w > 0 ? Math.round(Number(kg) / w) : "—";
};

const getBatchDisplayQuantities = (batch) => {
  const totalRaw = Number(batch?.dietOutNet);
  const totalKg = Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : null;

  const shippedRaw = Number(batch?.shippedKg);
  const shippedKg = Number.isFinite(shippedRaw) && shippedRaw >= 0 ? shippedRaw : 0;

  const leftRaw = Number(batch?.leftInStockKg);
  let leftKg = Number.isFinite(leftRaw) ? leftRaw : null;

  // Fallback to derived stock when backend field is missing or malformed.
  if (leftKg == null && totalKg != null) {
    leftKg = Math.max(totalKg - shippedKg, 0);
  }

  // Guard against negative/oversized values from legacy inconsistent records.
  if (leftKg != null) {
    leftKg = Math.max(leftKg, 0);
    if (totalKg != null) {
      leftKg = Math.min(leftKg, totalKg);
    }
  }

  return { totalKg, leftKg, shippedKg };
};

function BatchRow({ batch, blendName, versionName, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [chipAnchor, setChipAnchor] = useState(null);
  const { totalKg, leftKg, shippedKg } = getBatchDisplayQuantities(batch);

  const processingDate = batch.processingDate ?? batch.dietProcessingDate ?? batch.primaryProcessingDate;
  const fmtProcessingDate = processingDate
    ? (() => { try { return format(new Date(processingDate), "dd/MM/yyyy"); } catch { return String(processingDate); } })()
    : "—";

  return (
    <>
      <TableRow
        hover
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
        onClick={() => setOpen((p) => !p)}
      >
        <TableCell sx={{ width: 40, p: 0.5 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{batch.id}</TableCell>
        <TableCell>
          {batch.primaryId || batch.batchNumber || "—"}
          {batch.addBack && (
            <Chip
              label="Add Back"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }}
            />
          )}
        </TableCell>
        <TableCell>{fmtProcessingDate}</TableCell>
        <TableCell>{batch.addBack ? "— (Add Back)" : blendName}</TableCell>
        <TableCell>{batch.addBack ? "—" : versionName}</TableCell>
        <TableCell align="right">
          {totalKg != null ? totalKg.toLocaleString() : "—"}
        </TableCell>
        <TableCell align="right">
          {toCases(totalKg, batch.netWeightPerCase)}
        </TableCell>
        <TableCell align="right">
          {leftKg != null
            ? Number(leftKg).toLocaleString()
            : "—"}
        </TableCell>
        <TableCell align="right">
          {toCases(leftKg, batch.netWeightPerCase)}
        </TableCell>
        <TableCell>
          <StatusChip status={batch.recordStatus} />
        </TableCell>
        <TableCell>
          <ShipmentChip
            shippedKg={shippedKg}
            leftInStockKg={leftKg}
            onClick={(e) => { e.stopPropagation(); setChipAnchor(e.currentTarget); }}
          />
          <BatchAllocationsPopover
            batchId={batch.id}
            anchorEl={chipAnchor}
            onClose={() => setChipAnchor(null)}
          />
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(batch)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(batch)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Expandable detail */}
      <TableRow>
        <TableCell colSpan={13} sx={{ py: 0, px: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ px: 2, borderBottom: "1px solid #e0e0e0", minHeight: 36 }}
                TabIndicatorProps={{ style: { height: 3 } }}
              >
                <Tab label="Blend Items" sx={{ minHeight: 36, fontSize: "0.8rem" }} />
                <Tab label="Cost Components" sx={{ minHeight: 36, fontSize: "0.8rem" }} />
                <Tab label="Planned Costs (SI)" sx={{ minHeight: 36, fontSize: "0.8rem" }} />
                <Tab label="Planned vs Actual" sx={{ minHeight: 36, fontSize: "0.8rem" }} />
                <Tab label="Process Info" sx={{ minHeight: 36, fontSize: "0.8rem" }} />
                <Tab label="Cost Summary" sx={{ minHeight: 36, fontSize: "0.8rem", fontWeight: 600, color: "success.dark" }} />
              </Tabs>
              <Box sx={{ px: 2, py: 1.5 }}>
                {tab === 0 && (batch.addBack
                  ? <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>This is an Add Back batch — no blend items.</Typography>
                  : <BlendItemsPanel batch={batch} />)}
                {tab === 1 && <CostComponentsPanel batchId={batch.id} />}
                {tab === 2 && (batch.addBack
                  ? <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>Planned costs are not available for Add Back batches.</Typography>
                  : <PlannedCostsPanel batch={batch} />)}
                {tab === 3 && (batch.addBack
                  ? <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>Planned vs Actual is not available for Add Back batches.</Typography>
                  : <PlannedVsActualPanel batch={batch} />)}
                {tab === 4 && <ProcessInfoPanel batch={batch} />}
                {tab === 5 && <BatchCostSummaryPanel batch={batch} />}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

export default function BatchView() {
  // Filter state
  const [blends, setBlends] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedBlendIds, setSelectedBlendIds] = useState([]);
  const [selectedVersionIds, setSelectedVersionIds] = useState([]);
  const [loadingBlends, setLoadingBlends] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Table state
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Date filter
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Dialogs
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editBatch, setEditBatch] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Add Back dialog
  const [addBackOpen, setAddBackOpen] = useState(false);
  const [ab, setAb] = useState({});
  const [addBackSaving, setAddBackSaving] = useState(false);

  // Load blends on mount
  useEffect(() => {
    setLoadingBlends(true);
    retrieveAllBlendsApi()
      .then((res) => setBlends(res?.data || []))
      .catch(() => toast.error("Could not load blends."))
      .finally(() => setLoadingBlends(false));
  }, []);

  // Load versions when selected blends change
  useEffect(() => {
    setVersions([]);
    setSelectedVersionIds([]);
    setBatches([]);
    if (selectedBlendIds.length === 0) return;
    setLoadingVersions(true);
    Promise.all(
      selectedBlendIds.map((blendId) =>
        retrieveBlendVersionsApi(blendId)
          .then((res) => (res?.data || []).map((v) => ({ ...v, blendId })))
          .catch(() => [])
      )
    )
      .then((results) => setVersions(results.flat()))
      .catch(() => toast.error("Could not load versions."))
      .finally(() => setLoadingVersions(false));
  }, [selectedBlendIds]);

  // Load batches: when versions selected → those versions only; else all versions of selected blends
  const loadBatches = useCallback(async () => {
    if (selectedBlendIds.length === 0) {
      setBatches([]);
      return;
    }
    setLoadingBatches(true);
    try {
      const versionsToFetch = selectedVersionIds.length > 0
        ? versions.filter((v) => selectedVersionIds.includes(String(v.id)))
        : versions;
      const results = await Promise.all(
        versionsToFetch.map(async (ver) => {
          const blendId = ver.blendId ?? selectedBlendIds[0];
          const [batchRes, itemsRes] = await Promise.all([
            retrieveBatchesByBlendVersionApi(blendId, ver.id).catch(() => ({ data: [] })),
            retrieveVersionItemsApi(blendId, ver.id).catch(() => ({ data: [] })),
          ]);
          const netWeightPerCase = (itemsRes?.data || []).reduce((found, item) => {
            if (found) return found;
            const nwpc = Number(item.shippingInstruction?.shipmentRequest?.netWeightPerCase);
            return nwpc > 0 ? nwpc : null;
          }, null);
          return (batchRes?.data || []).map((b) => ({
            ...b,
            blendId: b.blendId ?? blendId,
            blendVersionId: b.blendVersionId ?? ver.id,
            blendName:
              b.blendName ||
              blends.find((bl) => String(bl.id) === String(blendId))?.name ||
              String(blendId),
            blendVersionName: b.blendVersionName || ver.versionName || String(ver.id),
            netWeightPerCase,
          }));
        })
      );
      setBatches(results.flat());
    } catch {
      toast.error("Could not load batches.");
    } finally {
      setLoadingBatches(false);
    }
  }, [selectedBlendIds, selectedVersionIds, blends, versions]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleOpenAdd = () => {
    setEditBatch(null);
    setAddEditOpen(true);
  };

  const handleOpenAddBack = () => {
    setAb({});
    setAddBackSaving(false);
    setAddBackOpen(true);
  };

  const abField = (key) => (e) => setAb((p) => ({ ...p, [key]: e.target.value }));
  const abN = (v) => (v !== undefined && v !== "" ? Number(v) : null);

  const handleSaveAddBack = async () => {
    if (!ab.dietOutNet || Number(ab.dietOutNet) <= 0) {
      toast.error("Please enter a valid DIET Out Net quantity.");
      return;
    }
    try {
      setAddBackSaving(true);
      const batch = {
        batchNumber:          ab.batchNumber          || null,
        primaryId:            ab.primaryId            || null,
        primaryCode:          ab.primaryCode          || null,
        primaryGradeName:     ab.primaryGradeName     || null,
        primaryProcessingDate: ab.primaryProcessingDate || null,
        rawIn:                abN(ab.rawIn),
        primaryOut:           abN(ab.primaryOut),
        dietIn:               abN(ab.dietIn),
        dietId:               ab.dietId              || null,
        dietCode:             ab.dietCode            || null,
        dietGradeName:        ab.dietGradeName        || null,
        dietProcessingDate:   ab.dietProcessingDate   || null,
        dietOutNet:           Number(ab.dietOutNet),
        tobaccoPgEur:         abN(ab.tobaccoPgEur),
        packagingMaterialEur: abN(ab.packagingMaterialEur),
        processingFeeEur:     abN(ab.processingFeeEur),
        totalCostEur:         abN(ab.totalCostEur),
        unitPriceEur:         abN(ab.unitPriceEur),
        invoicedValueEur:     abN(ab.invoicedValueEur),
        remark:               ab.remark              || null,
      };
      await createAddBackBatchApi({ shippingInstructionId: null, quantityToShip: null }, batch);
      toast.success("Add Back batch created. Link it to an SI via \"Link Existing Batch\".");
      setAddBackOpen(false);
      loadBatches();
    } catch (error) {
      console.error(error);
      toast.error("Add Back batch could not be created.");
    } finally {
      setAddBackSaving(false);
    }
  };

  const handleOpenEdit = (batch) => {
    setEditBatch(batch);
    setAddEditOpen(true);
  };

  const handleSaved = (_savedBatch, _blendId, _versionId) => {
    const nextBlendId = String(_blendId);
    const nextVersionId = String(_versionId);

    setSelectedBlendIds((prev) =>
      prev.includes(nextBlendId) ? prev : [nextBlendId, ...prev]
    );
    setSelectedVersionIds((prev) =>
      prev.includes(nextVersionId) ? prev : [nextVersionId, ...prev]
    );

    loadBatches();
  };

  const handleOpenDelete = async (batch) => {
    try {
      const res = await retrieveAllocationsByBatchApi(batch.id);
      const activeAllocations = res?.data || [];
       if (activeAllocations.length > 0) {
      const siNumbers = activeAllocations
        .map((a) => {
          const siNumber =
            a.finalShippingInstructionNumber || a.shippingInstructionNumber || "-";
          const siId = a.shippingInstructionId || "-";

          return `${siNumber} (ID: ${siId})`;
        })
        .join(", ");
        toast.error(
          `Cannot delete batch ${batch.batchNumber || batch.id}. It is allocated to SI(s): ${siNumbers}. De-allocate first.`,
        );
        return;
      }
    } catch {
      toast.error("Could not check batch allocations.");
      return;
    }
    setBatchToDelete(batch);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!batchToDelete) return;
    setDeleting(true);
    try {
      await deleteBatchApi(
        batchToDelete.blendId,
        batchToDelete.blendVersionId,
        batchToDelete.id
      );
      toast.success("Batch deleted.");
      setBatches((prev) => prev.filter((b) => b.id !== batchToDelete.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      const message = err?.response?.data?.message;
      toast.error(message || "Could not delete batch.");
    } finally {
      setDeleting(false);
    }
  };

  const blendLabel =
    selectedBlendIds.length === 0
      ? null
      : selectedBlendIds.length === 1
      ? blends.find((b) => String(b.id) === selectedBlendIds[0])?.name || selectedBlendIds[0]
      : `${selectedBlendIds.length} blends`;

  const versionLabel =
    selectedVersionIds.length === 0
      ? null
      : selectedVersionIds.length === 1
      ? versions.find((v) => String(v.id) === selectedVersionIds[0])?.versionName || selectedVersionIds[0]
      : `${selectedVersionIds.length} versions`;

  // Apply date filter client-side
  const filteredBatches = batches.filter((b) => {
    const dateVal = b.processingDate ?? b.dietProcessingDate ?? b.primaryProcessingDate;
    if (!dateVal) return true;
    const d = new Date(dateVal);
    if (isNaN(d)) return true;
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  const summaryTotals = filteredBatches.reduce(
    (acc, b) => {
      const { totalKg, leftKg } = getBatchDisplayQuantities(b);
      const nwpc = Number(b.netWeightPerCase);

      return {
        totalKg: acc.totalKg + (totalKg || 0),
        totalCases: acc.totalCases + (totalKg != null && nwpc > 0 ? Math.round(totalKg / nwpc) : 0),
        leftKg: acc.leftKg + (leftKg || 0),
      };
    },
    { totalKg: 0, totalCases: 0, leftKg: 0 }
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Page title ── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <InventoryIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          Batch Management
        </Typography>
      </Stack>

      {/* ── Filters ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <Autocomplete
            multiple
            sx={{ minWidth: 260 }}
            size="small"
            disabled={loadingBlends}
            options={blends}
            getOptionLabel={(b) => b.name || b.code || String(b.id)}
            value={blends.filter((b) => selectedBlendIds.includes(String(b.id)))}
            onChange={(_, vals) => setSelectedBlendIds(vals.map((b) => String(b.id)))}
            renderInput={(params) => (
              <TextField {...params} label="Blend" placeholder={selectedBlendIds.length === 0 ? "Search blend…" : ""} />
            )}
            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
            disableCloseOnSelect
          />

          <Autocomplete
            multiple
            sx={{ minWidth: 260 }}
            size="small"
            disabled={selectedBlendIds.length === 0 || loadingVersions}
            options={versions}
            getOptionLabel={(v) =>
              selectedBlendIds.length > 1
                ? `${blends.find((b) => String(b.id) === String(v.blendId))?.code || v.blendId} — ${v.versionName || v.id}`
                : v.versionName || String(v.id)
            }
            value={versions.filter((v) => selectedVersionIds.includes(String(v.id)))}
            onChange={(_, vals) => setSelectedVersionIds(vals.map((v) => String(v.id)))}
            renderInput={(params) => (
              <TextField {...params} label="Blend Version" placeholder={selectedVersionIds.length === 0 ? "All versions" : ""} />
            )}
            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
            disableCloseOnSelect
          />

          <TextField
            label="Processing Date From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
            disabled={selectedBlendIds.length === 0}
          />
          <TextField
            label="Processing Date To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
            disabled={selectedBlendIds.length === 0}
          />

          {(loadingBlends || loadingVersions) && (
            <CircularProgress size={20} />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={loadBatches}
                disabled={selectedBlendIds.length === 0 || loadingBatches}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            size="small"
          >
            Add Batch
          </Button>
          {/*<Button
            variant="outlined"
            color="warning"
            onClick={handleOpenAddBack}
            size="small"
          >
            Add Back Batch
          </Button>*/}
        </Stack>
      </Paper>

      {/* ── Table ── */}
      <Paper variant="outlined">
        <Toolbar
          sx={{
            pl: 2,
            pr: 1,
            bgcolor: "#f5f5f5",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "48px !important",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {blendLabel
              ? versionLabel
                ? `Batches — ${blendLabel} / ${versionLabel}`
                : `Batches — ${blendLabel} (all versions)`
              : "Batches"}
          </Typography>
          {filteredBatches.length > 0 && (
            <Chip
              label={filteredBatches.length}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
            />
          )}
        </Toolbar>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Batch Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Processing Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Blend</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Total (kg)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Total (cases)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Left in Stock (kg)</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Left in Stock (cases)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Shipment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingBatches ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : !selectedBlendIds.length ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary" variant="body2">
                      Select a blend to view batches.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredBatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary" variant="body2">
                      {batches.length > 0
                        ? "No batches match the selected date range."
                        : "No batches found for this selection."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBatches.map((batch) => (
                  <BatchRow
                    key={batch.id}
                    batch={batch}
                    blendName={batch.blendName || ""}
                    versionName={batch.blendVersionName || ""}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Summary row ── */}
      {filteredBatches.length > 0 && !loadingBatches && (
        <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
          <Stack direction="row" spacing={3}>
            <Typography variant="body2">
              <strong>Total batches:</strong> {filteredBatches.length}
              {filteredBatches.length !== batches.length && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({batches.length} total)
                </Typography>
              )}
            </Typography>
            <Typography variant="body2">
              <strong>Total (kg):</strong>{" "}
              {summaryTotals.totalKg.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <strong>Total (cases):</strong>{" "}
              {summaryTotals.totalCases}
            </Typography>
            <Typography variant="body2">
              <strong>Left in stock (kg):</strong>{" "}
              {summaryTotals.leftKg.toLocaleString()}
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* ── Add / Edit dialog ── */}
      <AddEditBatchDialog
        open={addEditOpen}
        onClose={() => setAddEditOpen(false)}
        onSaved={handleSaved}
        editBatch={editBatch}
        initialBlendId={selectedBlendIds[0] || undefined}
        initialVersionId={selectedVersionIds[0] || undefined}
      />

      {/* ── Add Back batch dialog ── */}
      <Dialog open={addBackOpen} onClose={() => setAddBackOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Back Batch</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Fields marked * are required. Add Back batches do not affect inventory.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 700 }}>Identification</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" label="Batch Number" value={ab.batchNumber || ""} onChange={abField("batchNumber")} />
            <TextField size="small" label="Primary ID" value={ab.primaryId || ""} onChange={abField("primaryId")} />
            <TextField size="small" label="Primary Code" value={ab.primaryCode || ""} onChange={abField("primaryCode")} />
            <TextField size="small" label="Primary Grade Name" value={ab.primaryGradeName || ""} onChange={abField("primaryGradeName")} />
            <TextField size="small" label="Primary Processing Date" type="date" InputLabelProps={{ shrink: true }} value={ab.primaryProcessingDate || ""} onChange={abField("primaryProcessingDate")} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>Primary Process</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" label="Raw In (kg)" type="number" value={ab.rawIn || ""} onChange={abField("rawIn")} />
            <TextField size="small" label="Primary Out (kg)" type="number" value={ab.primaryOut || ""} onChange={abField("primaryOut")} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>DIET Process</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" label="DIET ID" value={ab.dietId || ""} onChange={abField("dietId")} />
            <TextField size="small" label="DIET Code" value={ab.dietCode || ""} onChange={abField("dietCode")} />
            <TextField size="small" label="DIET Grade Name" value={ab.dietGradeName || ""} onChange={abField("dietGradeName")} />
            <TextField size="small" label="DIET Processing Date" type="date" InputLabelProps={{ shrink: true }} value={ab.dietProcessingDate || ""} onChange={abField("dietProcessingDate")} />
            <TextField size="small" label="DIET In (kg)" type="number" value={ab.dietIn || ""} onChange={abField("dietIn")} />
            <TextField size="small" label="DIET Out Net (kg) *" type="number" required value={ab.dietOutNet || ""} onChange={abField("dietOutNet")} />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 700 }}>Costs (EUR)</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" label="Tobacco + PG (EUR)" type="number" value={ab.tobaccoPgEur || ""} onChange={abField("tobaccoPgEur")} />
            <TextField size="small" label="Packaging Material (EUR)" type="number" value={ab.packagingMaterialEur || ""} onChange={abField("packagingMaterialEur")} />
            <TextField size="small" label="Processing Fee (EUR)" type="number" value={ab.processingFeeEur || ""} onChange={abField("processingFeeEur")} />
            <TextField size="small" label="Total Cost (EUR)" type="number" value={ab.totalCostEur || ""} onChange={abField("totalCostEur")} />
            <TextField size="small" label="Unit Price (EUR/kg)" type="number" value={ab.unitPriceEur || ""} onChange={abField("unitPriceEur")} />
            <TextField size="small" label="Invoiced Value (EUR)" type="number" value={ab.invoicedValueEur || ""} onChange={abField("invoicedValueEur")} />
          </Box>

          <TextField fullWidth size="small" label="Remark" multiline rows={2} sx={{ mt: 2 }} value={ab.remark || ""} onChange={abField("remark")} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBackOpen(false)} disabled={addBackSaving}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSaveAddBack} disabled={addBackSaving}>
            {addBackSaving ? <CircularProgress size={18} /> : "Create Add Back"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Batch</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete batch{" "}
            <strong>
              {batchToDelete?.batchNumber || batchToDelete?.id}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
